/**
 * One source's findings on their way to strategies (spec account-persona 11 U6): its calls and principles, the ones
 * validated out of sample (what its Scout reliability counts), the strategies drafted from them, measured by the desk,
 * and admitted to a bucket. Template example: NextAdmin `(dashboard)/(home)/_component/top-products` (a `Card` of
 * `ItemList` rows), as the call and principle funnels.
 */
import type React from 'react';
import { Card, ItemList, type ItemListItem } from '../../../components/ui/index.js';
import type { ReasoningCallFunnel, ReasoningPrincipleFunnel } from '../../../generated/api/types.gen.js';

type FunnelCount = NonNullable<ReasoningPrincipleFunnel['module']>;

const share = (part: number, whole: number) => (whole > 0 ? ` (${Math.round((part / whole) * 100)} %)` : '');

export const ReasoningFindingsFunnelCard: React.FC<{
  handle: string;
  calls: ReasoningCallFunnel | undefined;
  principles: ReasoningPrincipleFunnel;
  count: FunnelCount;
}> = ({ handle, calls, principles, count }) => {
  const liveCalls = calls ? calls.calls - calls.callsDismissed : 0;
  const { validated } = count;
  const callsValidated = validated.calls.held + validated.calls.broke;
  const principlesValidated = validated.principles.held + validated.principles.broke;
  const items: ItemListItem[] = [
    {
      id: 'findings',
      title: 'Findings',
      subtitle: `${liveCalls} calls and ${principles.principles} principles`,
      value: liveCalls + principles.principles,
    },
    {
      id: 'validated',
      title: 'Validated out of sample',
      subtitle: `Calls ${validated.calls.held} held, ${validated.calls.broke} broke (independent, horizon stated); principles ${validated.principles.held} held, ${validated.principles.broke} broke`,
      value: callsValidated + principlesValidated,
    },
    {
      id: 'drafted',
      title: 'Strategies drafted',
      subtitle: 'From proven bundles and principles that held: research drafts, never a book',
      value: count.drafted,
    },
    {
      id: 'backtested',
      title: 'Measured by the desk',
      subtitle: "The desk's own proof of their edge",
      value: `${count.backtested}${share(count.backtested, count.drafted)}`,
    },
    {
      id: 'admitted',
      title: 'Admitted to a bucket',
      subtitle: 'Only by an admin-approved request',
      value: count.admitted,
    },
  ];
  return (
    <Card title={`${handle} · findings to strategies`} className="xl:col-span-6">
      <ItemList items={items} />
    </Card>
  );
};
